import LoyaltyTierLabel from 'components/Common/Labels/LoyaltyTierLabel'
import Pane from 'components/Common/Pane'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LineCheckIcon from 'LuxKit/Icons/line/LineCheckIcon'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import Colour from 'components/utils/Colour'
import Group from 'components/utils/Group'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import getLoyaltyEligibleTier from 'loyalty/selectors/getLoyaltyEligibleTier'
import getLoyaltyTierDisplayName from 'loyalty/selectors/getLoyaltyTierDisplayName'
import { rem } from 'polished'
import React, { ComponentProps, FormEventHandler, useCallback, useMemo } from 'react'
import RealisticConfetti from 'react-canvas-confetti/dist/presets/realistic'
import styled from 'styled-components'
import LoyaltyProductLogo from './LoyaltyProductLogo'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import CheckboxInput from 'LuxKit/Checkbox/CheckboxInput'
import { SubscriptionSource } from 'api/LESubscriptions'
import { updateLESubscriptionsV2 } from 'actions/LESubscriptionsActions'
import AccountAccessLegalText from 'components/Account/AccountAccess/AccountAccessLegalText'
import LineChairWithAirplaneIcon from 'LuxKit/Icons/line/LineChairWithAirplaneIcon'
import LineCrownIcon from 'LuxKit/Icons/line/LineCrownIcon'
import LineStarIcon from 'LuxKit/Icons/line/LineStarIcon'
import LineTaxiIcon from 'LuxKit/Icons/line/LineTaxiIcon'
import useLoyaltyConfig from 'loyalty/hooks/useLoyaltyConfig'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import LineShieldCheckIcon from 'LuxKit/Icons/line/LineShieldCheckIcon'
import LinePhoneIcon from 'LuxKit/Icons/line/LinePhoneIcon'
import Plural from 'components/utils/Formatters/Plural'

const Hero = styled(Pane)`
  padding: ${rem(32)} ${rem(24)};

  &.tier-bronze {
    background: ${props => props.theme.palette.product.loyalty.bronze.gradientPrimaryBackground};
  }
  &.tier-silver {
    background: ${props => props.theme.palette.product.loyalty.silver.gradientPrimaryBackground};
  }
  &.tier-gold {
    background: ${props => props.theme.palette.product.loyalty.gold.gradientPrimaryBackground};
  }
  &.tier-platinum {
    background: ${props => props.theme.palette.product.loyalty.platinum.gradientPrimaryBackground};
  }
`

interface CopyContent {
  content: React.ReactNode
  icon: React.ReactElement
  enabled: boolean
}

function getBenefitsCopy(
  enabledBenefits: App.LoyaltyRegionEnabledBenefits,
  regionTierBenefits: App.LoyaltyTierRegionEnabledBenefits
): Utils.PartialRecord<App.LoyaltyTier, Array<CopyContent>> {
  const silverHotelUpgradeBenefit = regionTierBenefits.silver.find(benefit => benefit.code === 'hotel_upgrade')
  const goldHotelUpgradeBenefit = regionTierBenefits.gold.find(benefit => benefit.code === 'hotel_upgrade')

  const goldAirportTransferBenefit = regionTierBenefits.gold.find(benefit => benefit.code === 'airport_transfer')

  const silverLoungePassBenefit = regionTierBenefits.silver.find(benefit => benefit.code === 'lounge_pass')
  const goldLoungePassBenefit = regionTierBenefits.gold.find(benefit => benefit.code === 'lounge_pass')

  const silverDiscountedInsuranceBenefit = regionTierBenefits.silver.find(benefit => benefit.code === 'discounted_insurance')
  const goldDiscountedInsuranceBenefit = regionTierBenefits.gold.find(benefit => benefit.code === 'discounted_insurance')
  return {
    bronze: [
      { content: 'Gain Status Credits with every booking from day one', icon: <LineCheckIcon />, enabled: true },
      { content: 'Earn and use points across all products', icon: <LineCheckIcon />, enabled: true },
      { content: 'Double dip points with other loyalty programs', icon: <LineCheckIcon />, enabled: true },
    ],
    silver: [
      { content: <>{silverHotelUpgradeBenefit?.unlimited ? 'Unlimited' : `${silverHotelUpgradeBenefit?.limit}x`} guaranteed free room or package <Plural count={silverHotelUpgradeBenefit?.limit ?? 0} singular="upgrade" /></>, icon: <LineCrownIcon />, enabled: enabledBenefits.hotel_upgrade },
      { content: <>{silverLoungePassBenefit?.unlimited ? 'Unlimited' : `${silverLoungePassBenefit?.limit}x`} free lounge <Plural count={silverLoungePassBenefit?.limit ?? 0} singular="pass" plural="passes" /> at select airports</>, icon: <LineChairWithAirplaneIcon />, enabled: enabledBenefits.lounge_pass },
      { content: <>{silverDiscountedInsuranceBenefit?.value?.amount ?? 0}% discounted travel insurance</>, icon: <LineShieldCheckIcon />, enabled: enabledBenefits.discounted_insurance },
      { content: 'Priority phone support 24/7', icon: <LinePhoneIcon />, enabled: enabledBenefits.phone_support },
      { content: 'Earn and use points across all products', icon: <LineStarIcon />, enabled: true },
    ],
    gold: [
      { content: <>{goldHotelUpgradeBenefit?.unlimited ? 'Unlimited' : `${goldHotelUpgradeBenefit?.limit}x`} guaranteed free room or package <Plural count={goldHotelUpgradeBenefit?.limit ?? 0} singular="upgrade" /></>, icon: <LineCrownIcon />, enabled: enabledBenefits.hotel_upgrade },
      { content: <>{goldAirportTransferBenefit?.unlimited ? 'Unlimited' : `${goldAirportTransferBenefit?.limit}x`} free airport chauffeur service</>, icon: <LineTaxiIcon />, enabled: enabledBenefits.airport_transfer },
      { content: <>{goldLoungePassBenefit?.unlimited ? 'Unlimited' : `${goldLoungePassBenefit?.limit}x`} free lounge <Plural count={goldLoungePassBenefit?.limit ?? 0} singular="pass" plural="passes" /> at select airports</>, icon: <LineChairWithAirplaneIcon />, enabled: enabledBenefits.lounge_pass },
      { content: <>{goldDiscountedInsuranceBenefit?.value?.amount ?? 0}% discounted travel insurance</>, icon: <LineShieldCheckIcon />, enabled: enabledBenefits.discounted_insurance },
      { content: 'Priority phone support 24/7', icon: <LinePhoneIcon />, enabled: enabledBenefits.phone_support },
    ],
  }
}

const CONFETTI_COLOURS: Record<App.LoyaltyTier, Array<string>> = {
  bronze: ['#EFE7E3', '#D4B5A4', '#91674E'],
  silver: ['#E7E8EA', '#BABCC2', '#6D717C'],
  gold: ['#EFE8D1', '#C6BC92', '#78704E'],
  platinum: [],
}

const CONFETTI_AUTO_RUN: NonNullable<ComponentProps<typeof RealisticConfetti>['autorun']> = { duration: 5, delay: 1, speed: 1 }

interface Props {
  onAfterClose: React.ComponentProps<typeof ModalBase>['onAfterClose']
  primaryActionCta: string
  secondaryActionCta?: string
}

function LoyaltyJoinModal(props: Props) {
  const {
    onAfterClose,
    primaryActionCta,
    secondaryActionCta,
  } = props

  const dispatch = useAppDispatch()
  const userId = useAppSelector(state => state.auth.account.memberId)
  const isEmailSubscribed = useAppSelector(state => state.leSubscriptions.settings.emailSubscribed)
  const modalElementContext = useModalElementContext<boolean>()

  const primaryActionProps: React.ComponentProps<typeof ModalFooter>['primaryActionProps'] = useMemo(() => ({
    children: primaryActionCta,
    type: 'submit',
  }), [primaryActionCta])

  const secondaryActionProps: React.ComponentProps<typeof ModalFooter>['secondaryActionProps'] = useMemo(() => secondaryActionCta ? {
    children: secondaryActionCta,
  } : undefined, [secondaryActionCta])

  const handleSubmission = useCallback<FormEventHandler<HTMLFormElement>>((event) => {
    event.preventDefault()
    if (!isEmailSubscribed && userId) {
      dispatch(updateLESubscriptionsV2({
        userId,
        subscriptions: {
          smsSubscribed: true,
          emailSubscribed: true,
          appPushSubscribed: true,
          myJourneySubscribed: true,
          todaysEscapesSubscribed: true,
          curatedCollectionSubscribed: true,
          todaysEscapesCadence: 'DAILY',
        },
        source: SubscriptionSource.LOYALTY_ENROL_MODAL,
      }))
    }
    modalElementContext.resolve(true)
  }, [dispatch, isEmailSubscribed, modalElementContext, userId])

  const accountEligibleTier = useAppSelector(getLoyaltyEligibleTier)
  const givenName = useAppSelector((state: App.State) => state.auth.account.givenName)
  const tierName = getLoyaltyTierDisplayName(accountEligibleTier)

  const confettiColours = CONFETTI_COLOURS[accountEligibleTier]
  const confettiDecoration = useCallback<NonNullable<ComponentProps<typeof RealisticConfetti>['decorateOptions']>>((options) => {
    return {
      ...options,
      colors: confettiColours,
      origin: { y: 0.45, x: 0.5 },
      zIndex: 999,
    }
  }, [confettiColours])

  const [config, configFetching, configError] = useLoyaltyConfig()

  const benefitsCopy = useMemo(() => {
    if (!config || configError) return
    const tierCopy = getBenefitsCopy(config.regionEnabledBenefits, config.regionTierBenefits)?.[accountEligibleTier]
    if (!tierCopy) return
    return tierCopy.filter(benefit => benefit.enabled)
  }, [config, configError, accountEligibleTier])

  if (configFetching) return <LoadingIndicator />
  if (!benefitsCopy) return null

  // Users will only be eligible for bronze, silver, or gold at launch
  if (accountEligibleTier === 'platinum') return null

  return <ProductPaletteProvider palette={`tier-${accountEligibleTier}`}>
    <ModalBase dismissible onAfterClose={onAfterClose}>
      <form onSubmit={handleSubmission}>
        <ModalBody>
          <Hero className={`tier-${accountEligibleTier}`}>
            <Group direction="vertical" gap={16}>
              <Group direction="vertical" gap={12}>
                <Group direction="horizontal" gap={12} verticalAlign="center">
                  <LoyaltyProductLogo height={24} />
                  <LoyaltyTierLabel kind="primary" tier={accountEligibleTier} format="uppercase">Exclusive Invite</LoyaltyTierLabel>
                </Group>
                <BodyText variant="small">The ultimate rewards program to elevate every escape</BodyText>
              </Group>
              <div>
                <Heading variant="heading2">
                  {!!givenName && <><i>{givenName}</i>, you've unlocked</>}
                  {!givenName && <>You've unlocked</>}
                  <br />
                  <Colour value={`tier-${accountEligibleTier}-light-contrast`}>
                    {tierName} status
                  </Colour>
                </Heading>
              </div>
            </Group>
          </Hero>
          <ModalContent>
            <VerticalSpacer gap={12}>
              <BodyText variant="large" weight="bold" colour="neutral-two">
                Enjoy instant benefits as a {tierName} member
              </BodyText>
              {benefitsCopy.length && <VerticalSpacer gap={4}>
                {benefitsCopy.map((benefit, idx) => <BodyTextBlock
                  key={idx}
                  variant="medium"
                  startIcon={benefit.icon}
                >
                  {benefit.content}
                </BodyTextBlock>)}
              </VerticalSpacer>}
              {!isEmailSubscribed && (
                <CheckboxInput
                  size="small"
                  required
                  data-testid="loyalty-join-accept-terms"
                  name="confirmTermsAndSubscribe"
                >
                  <AccountAccessLegalText />
                </CheckboxInput>
              )}
            </VerticalSpacer>
          </ModalContent>
        </ModalBody>
        <RealisticConfetti autorun={CONFETTI_AUTO_RUN} decorateOptions={confettiDecoration} />
        <ModalFooter
          primaryActionProps={primaryActionProps}
          secondaryActionProps={secondaryActionProps}
        />
      </form>
    </ModalBase>
  </ProductPaletteProvider>
}

export default LoyaltyJoinModal
